import { RefObject, useEffect, useRef } from 'react';

type TEventName =
  | 'tgrclick'
  | 'tgrblur'
  | 'tgrchange'
  | 'tgrfocus'
  | 'tgriconclick'
  | 'tgrinput'
  | 'tgrselectopen';

export function useCustomEventListener<
  TEvent = Event,
  TElement extends EventTarget = HTMLElement,
>(
  targetRef: RefObject<TElement>,
  eventName: TEventName,
  handler: (event: TEvent) => void,
  options?: AddEventListenerOptions | boolean,
  enabled = true,
) {
  const savedHandler = useRef<(event: TEvent) => void>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) {
      return () => {
        // empty function
      };
    }

    const element = targetRef.current;

    const isSupported = element && element.addEventListener;
    if (!isSupported) {
      if (element) {
        console.warn('O elemento alvo não suporta addEventListener:', element);
      }

      return () => {
        // empty function
      };
    }

    const eventListener = (event: Event) => {
      if (savedHandler.current) {
        savedHandler.current(event as TEvent);
      }
    };

    element.addEventListener(eventName, eventListener, options);

    return () => {
      element.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, targetRef, options, enabled]);
}
